/**
 * Main application state store using Zustand.
 * Manages curriculum progress, simulation state, user trading state, and UI state.
 */

import { create } from 'zustand'
import type {
  Market,
  Order,
  NewOrder,
  Position,
  OrderBook,
  Trade,
  OrderSide,
  MatchResult,
} from '../engine/types'
import type { SimulationSpeed } from '../engine/time'
import { MatchingEngine } from '../engine/matching'
import { getAvailableYesShares, updatePositions } from '../engine/positions'
import {
  loadPersistedState,
  savePersistedState,
  type PersistedState,
  type VisualizationMode,
} from './persistence'
import {
  getLesson,
  getAllLessons,
  instantiateScenario,
  cloneOrderBook,
  type Lesson,
  type SimulationScenario,
} from '../curriculum/manager'
import { MarketSimulator, DEFAULT_SIMULATION_CONFIG } from '../engine/simulation'
import { SPEED_MULTIPLIERS, TimeSimulationEngine } from '../engine/time'

// =============================================================================
// Error Types
// =============================================================================

/**
 * User-facing error types for display in the UI.
 * Each error type includes relevant context for actionable error messages.
 */
export type UserError =
  | { type: 'insufficient-balance'; required: number; available: number }
  | { type: 'insufficient-shares'; required: number; available: number }
  | { type: 'invalid-order'; reason: string }
  | { type: 'invalid-price'; message: string }
  | { type: 'invalid-quantity'; message: string }
  | { type: 'no-liquidity'; side: 'buy' | 'sell' }
  | { type: 'storage-full' }
  | { type: 'storage-corrupted' }
  | { type: 'unknown'; message: string }

/**
 * Information about the last trade execution for UI display.
 */
export interface LastExecution {
  trades: Trade[]
  side: OrderSide
  totalQuantity: number
}

/**
 * An event in the activity timeline.
 */
export interface TimelineEvent {
  id: string
  type: 'order' | 'trade'
  timestamp: number
  side: OrderSide
  price: number
  quantity: number
  orderType?: 'limit' | 'market'
  isUserOrder?: boolean
}

// =============================================================================
// State Interface
// =============================================================================

export interface AppState {
  // Curriculum state
  currentLessonId: string | null
  completedLessons: Set<string>

  // Simulation state
  market: Market | null
  activeScenario: SimulationScenario | null
  simulationSpeed: SimulationSpeed
  simulationTime: number

  // User trading state
  balance: number
  positions: Position[]
  orderHistory: Order[]
  openOrders: Order[]

  // UI state
  visualizationMode: VisualizationMode
  activeTutorialHint: string | null
  error: UserError | null
  debugMode: boolean
  lastExecution: LastExecution | null
  timelineEvents: TimelineEvent[]

  // Actions - Curriculum
  startLesson: (lessonId: string) => void
  completeLesson: (lessonId: string) => void
  setDebugMode: (enabled: boolean) => void
  skipToLesson: (lessonId: string) => void

  // Actions - Trading
  placeOrder: (order: NewOrder) => void
  cancelOrder: (orderId: string) => void

  // Actions - Simulation
  setSimulationSpeed: (speed: SimulationSpeed) => void
  setSimulationTime: (time: number) => void
  loadScenario: (scenario: SimulationScenario) => void
  resetSimulation: () => void
  setMarket: (market: Market) => void

  // Actions - UI
  setVisualizationMode: (mode: VisualizationMode) => void
  setActiveTutorialHint: (hint: string | null) => void
  setError: (error: UserError | null) => void
  clearError: () => void
  clearLastExecution: () => void
}

// =============================================================================
// Matching Engine Instance
// =============================================================================

const matchingEngine = new MatchingEngine()

// =============================================================================
// Market Simulator Instance (for bot trading activity)
// =============================================================================

let marketSimulator: MarketSimulator | null = null
let timeEngine: TimeSimulationEngine | null = null

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create an empty order book.
 */
function createEmptyOrderBook(): OrderBook {
  return {
    bids: [],
    asks: [],
    trades: [],
    lastPrice: null,
  }
}

/**
 * Persist progress-related state to localStorage.
 */
function persistProgress(state: AppState): void {
  const persisted = loadPersistedState()
  const updated: PersistedState = {
    ...persisted,
    progress: {
      ...persisted.progress,
      completedLessons: Array.from(state.completedLessons),
      currentLesson: state.currentLessonId,
      lastAccessed: Date.now(),
    },
    settings: {
      ...persisted.settings,
      visualizationMode: state.visualizationMode,
      simulationSpeed: state.simulationSpeed === 'paused' ? 'normal' : state.simulationSpeed,
    },
  }
  savePersistedState(updated)
}

// =============================================================================
// Store Creation
// =============================================================================

/**
 * Load initial state from persistence.
 */
function getInitialState(): Partial<AppState> {
  const persisted = loadPersistedState()

  return {
    currentLessonId: persisted.progress.currentLesson,
    completedLessons: new Set(persisted.progress.completedLessons),
    visualizationMode: persisted.settings.visualizationMode,
    simulationSpeed: 'paused', // Always start paused
  }
}

function findLessonScenario(lesson?: Lesson): SimulationScenario | null {
  if (!lesson) {
    return null
  }

  for (const content of lesson.content) {
    if (content.type === 'interactive' || content.type === 'sandbox') {
      return content.data.scenario
    }
  }

  return null
}

function cloneMarket(market: Market): Market {
  return {
    ...market,
    orderBook: cloneOrderBook(market.orderBook),
  }
}

function findOrderInBook(orderBook: OrderBook, orderId: string): Order | undefined {
  return orderBook.bids.find((order) => order.id === orderId)
    ?? orderBook.asks.find((order) => order.id === orderId)
}

function getOrderCashDelta(trade: Trade, orderId: string): number {
  if (trade.buyOrderId === orderId) {
    return -trade.quantity * trade.price
  }

  if (trade.sellOrderId === orderId) {
    return trade.quantity * trade.price
  }

  return 0
}

function buildTimelineEvents(
  order: NewOrder,
  result: MatchResult,
  isUserOrder: boolean
): TimelineEvent[] {
  const events: TimelineEvent[] = [
    {
      id: `order-${result.updatedOrder.id}`,
      type: 'order',
      timestamp: result.updatedOrder.timestamp,
      side: order.side,
      price: order.price,
      quantity: order.quantity,
      orderType: order.type,
      isUserOrder,
    },
  ]

  for (const trade of result.trades) {
    events.push({
      id: `trade-${trade.id}`,
      type: 'trade',
      timestamp: trade.timestamp,
      side: trade.takerSide,
      price: trade.price,
      quantity: trade.quantity,
      isUserOrder,
    })
  }

  return events
}

function reconcileOrderResult(
  state: AppState,
  market: Market,
  order: NewOrder,
  result: MatchResult,
  isUserSubmission: boolean
): Pick<AppState, 'balance' | 'positions' | 'orderHistory' | 'openOrders' | 'lastExecution'> {
  const nextOrderHistory = isUserSubmission
    ? [...state.orderHistory, { ...result.updatedOrder }]
    : [...state.orderHistory]
  const knownUserOrderIds = new Set([
    ...state.openOrders.map((existingOrder) => existingOrder.id),
    ...nextOrderHistory.map((existingOrder) => existingOrder.id),
  ])
  const orderUpdates = new Map<string, Order>()
  let balance = state.balance
  let positions = [...state.positions]

  if (isUserSubmission) {
    orderUpdates.set(result.updatedOrder.id, { ...result.updatedOrder })
  }

  for (const trade of result.trades) {
    for (const orderId of [trade.buyOrderId, trade.sellOrderId]) {
      if (!knownUserOrderIds.has(orderId)) {
        continue
      }

      balance += getOrderCashDelta(trade, orderId)
      positions = updatePositions(positions, trade, orderId, market)

      if (!orderUpdates.has(orderId)) {
        const liveOrder = findOrderInBook(market.orderBook, orderId)
        if (liveOrder) {
          orderUpdates.set(orderId, { ...liveOrder })
          continue
        }

        const existingOrder = nextOrderHistory.find((historyOrder) => historyOrder.id === orderId)
          ?? state.openOrders.find((openOrder) => openOrder.id === orderId)
        if (existingOrder) {
          orderUpdates.set(orderId, {
            ...existingOrder,
            status: 'filled',
            filledQuantity: existingOrder.quantity,
          })
        }
      }
    }
  }

  const orderHistory = nextOrderHistory.map((historyOrder) => (
    orderUpdates.get(historyOrder.id) ?? historyOrder
  ))
  const openOrders = orderHistory.filter(
    (historyOrder) => historyOrder.status === 'open' || historyOrder.status === 'partial'
  )

  const lastExecution = isUserSubmission
    ? (
        result.trades.length > 0
          ? {
              trades: result.trades,
              side: order.side,
              totalQuantity: order.quantity,
            }
          : null
      )
    : state.lastExecution

  return {
    balance,
    positions,
    orderHistory,
    openOrders,
    lastExecution,
  }
}

/**
 * The main application store.
 */
export const useAppStore = create<AppState>((set, get) => {
  const initial = getInitialState()

  return {
    // Initial curriculum state
    currentLessonId: initial.currentLessonId ?? null,
    completedLessons: initial.completedLessons ?? new Set(),

    // Initial simulation state
    market: null,
    activeScenario: null,
    simulationSpeed: 'paused',
    simulationTime: 0,

    // Initial user state
    balance: 100, // Default starting balance
    positions: [],
    orderHistory: [],
    openOrders: [],

    // Initial UI state
    visualizationMode: initial.visualizationMode ?? 'beginner',
    activeTutorialHint: null,
    error: null,
    debugMode: false,
    lastExecution: null,
    timelineEvents: [],

    // ==========================================================================
    // Curriculum Actions
    // ==========================================================================

    startLesson: (lessonId: string) => {
      const lesson = getLesson(lessonId)
      const scenario = findLessonScenario(lesson)
      const scenarioState = scenario ? instantiateScenario(scenario) : null

      marketSimulator?.stop()
      marketSimulator = null
      timeEngine?.reset()

      set({
        currentLessonId: lessonId,
        market: scenarioState?.market ?? null,
        activeScenario: scenario,
        balance: scenarioState?.balance ?? 100,
        simulationSpeed: 'paused',
        simulationTime: 0,
        positions: [],
        openOrders: [],
        orderHistory: [],
        error: null,
        lastExecution: null,
        timelineEvents: [],
      })
      persistProgress(get())
    },

    completeLesson: (lessonId: string) => {
      set(state => {
        const newCompleted = new Set(state.completedLessons)
        newCompleted.add(lessonId)
        return { completedLessons: newCompleted }
      })
      persistProgress(get())
    },

    setDebugMode: (enabled: boolean) => {
      set({ debugMode: enabled })
    },

    skipToLesson: (lessonId: string) => {
      const lesson = getLesson(lessonId)
      if (!lesson) return

      // Mark all prerequisite lessons as complete
      const allLessons = getAllLessons()
      const lessonIndex = allLessons.findIndex(l => l.id === lessonId)
      const newCompleted = new Set(get().completedLessons)

      for (let i = 0; i < lessonIndex; i++) {
        newCompleted.add(allLessons[i].id)
      }

      set({ completedLessons: newCompleted })
      persistProgress(get())

      // Now start the lesson (this will load the market)
      get().startLesson(lessonId)
    },

    // ==========================================================================
    // Trading Actions
    // ==========================================================================

    placeOrder: (order: NewOrder) => {
      const state = get()

      if (!state.market) {
        set({ error: { type: 'unknown', message: 'No market available' } })
        return
      }

      const availableShares = getAvailableYesShares(
        state.positions,
        state.market.id,
        state.openOrders
      )

      // Process the order through the matching engine
      const result = matchingEngine.processOrder(
        order,
        state.market.orderBook,
        {
          balance: state.balance,
          availableShares,
        }
      )

      // Handle rejected orders
      if (result.updatedOrder.status === 'rejected') {
        const reason = result.updatedOrder.rejectionReason

        let error: UserError
        switch (reason) {
          case 'insufficient-balance':
            error = {
              type: 'insufficient-balance',
              required: order.quantity * (order.type === 'limit' ? order.price : 1),
              available: state.balance,
            }
            break
          case 'insufficient-shares':
            error = {
              type: 'insufficient-shares',
              required: order.quantity,
              available: availableShares,
            }
            break
          case 'invalid-price':
            error = { type: 'invalid-price', message: 'Price must be between $0.01 and $0.99' }
            break
          case 'invalid-quantity':
            error = { type: 'invalid-quantity', message: 'Quantity must be a positive whole number' }
            break
          case 'no-liquidity':
            error = { type: 'no-liquidity', side: order.side }
            break
          default:
            error = { type: 'invalid-order', reason: reason ?? 'Unknown error' }
        }

        set({ error })
        return
      }
      const reconciled = reconcileOrderResult(state, state.market, order, result, true)
      const timelineEvents = [
        ...buildTimelineEvents(order, result, true),
        ...state.timelineEvents,
      ].slice(0, 50)

      set({
        market: cloneMarket(state.market),
        balance: reconciled.balance,
        positions: reconciled.positions,
        orderHistory: reconciled.orderHistory,
        openOrders: reconciled.openOrders,
        error: null,
        lastExecution: reconciled.lastExecution,
        timelineEvents,
      })
    },

    cancelOrder: (orderId: string) => {
      const state = get()

      if (!state.market) {
        return
      }

      const result = matchingEngine.cancelOrder(orderId, state.market.orderBook)

      if (result.success) {
        set({
          market: {
            ...state.market,
            orderBook: result.book,
          },
          orderHistory: state.orderHistory.map((order) => (
            order.id === orderId ? { ...order, status: 'cancelled' } : order
          )),
          openOrders: state.openOrders.filter(o => o.id !== orderId),
        })
      }
    },

    // ==========================================================================
    // Simulation Actions
    // ==========================================================================

    setSimulationSpeed: (speed: SimulationSpeed) => {
      set({ simulationSpeed: speed })

      // Control the time engine
      if (!timeEngine) {
        timeEngine = new TimeSimulationEngine((time: number) => {
          set({ simulationTime: time })
        })
      }
      timeEngine.setSpeed(speed)

      // Control the market simulator based on speed
      const state = get()
      if (speed === 'paused') {
        // Stop the simulator
        marketSimulator?.stop()
      } else if (state.market) {
        // Start or update the simulator
        const speedMultiplier = SPEED_MULTIPLIERS[speed]

        if (!marketSimulator) {
          // Create simulator with a callback that places bot orders
          marketSimulator = new MarketSimulator(
            (order: NewOrder) => {
              // Process bot order through matching engine (simplified - no position tracking for bots)
              const currentState = get()
              if (!currentState.market) return

              const result = matchingEngine.processOrder(
                order,
                currentState.market.orderBook,
                {
                  balance: Infinity,
                  availableShares: Infinity,
                }
              )

              if (result.updatedOrder.status !== 'rejected') {
                const reconciled = reconcileOrderResult(
                  currentState,
                  currentState.market,
                  order,
                  result,
                  false
                )
                const timelineEvents = [
                  ...buildTimelineEvents(order, result, false),
                  ...currentState.timelineEvents,
                ].slice(0, 50)

                set({
                  market: cloneMarket(currentState.market),
                  balance: reconciled.balance,
                  positions: reconciled.positions,
                  orderHistory: reconciled.orderHistory,
                  openOrders: reconciled.openOrders,
                  lastExecution: reconciled.lastExecution,
                  timelineEvents,
                })
              }
            },
            () => get().market?.orderBook ?? null,
            {
              ...DEFAULT_SIMULATION_CONFIG,
              baseProbability: state.market.orderBook.lastPrice ?? 0.5,
            }
          )
        }

        marketSimulator.start(speedMultiplier)
      }

      // Persist non-paused speed preference
      if (speed !== 'paused') {
        persistProgress(get())
      }
    },

    setSimulationTime: (time: number) => {
      set({ simulationTime: time })
    },

    loadScenario: (scenario: SimulationScenario) => {
      marketSimulator?.stop()
      marketSimulator = null
      timeEngine?.reset()

      const { market, balance } = instantiateScenario(scenario)
      set({
        market,
        activeScenario: scenario,
        simulationSpeed: 'paused',
        simulationTime: 0,
        balance,
        positions: [],
        orderHistory: [],
        openOrders: [],
        error: null,
        lastExecution: null,
        timelineEvents: [],
      })
    },

    resetSimulation: () => {
      // Stop the simulator
      marketSimulator?.stop()
      marketSimulator = null
      timeEngine?.reset()

      const state = get()
      if (state.activeScenario) {
        const { market, balance } = instantiateScenario(state.activeScenario)
        set({
          market,
          activeScenario: state.activeScenario,
          simulationSpeed: 'paused',
          simulationTime: 0,
          balance,
          positions: [],
          orderHistory: [],
          openOrders: [],
          error: null,
          lastExecution: null,
          timelineEvents: [],
        })
        return
      }

      set({
        market: state.market ? {
          ...state.market,
          orderBook: createEmptyOrderBook(),
          resolved: false,
          resolution: null,
        } : null,
        activeScenario: null,
        simulationSpeed: 'paused',
        simulationTime: 0,
        balance: 100,
        positions: [],
        orderHistory: [],
        openOrders: [],
        error: null,
        lastExecution: null,
        timelineEvents: [],
      })
    },

    setMarket: (market: Market) => {
      set({
        market,
        activeScenario: null,
      })
    },

    // ==========================================================================
    // UI Actions
    // ==========================================================================

    setVisualizationMode: (mode: VisualizationMode) => {
      set({ visualizationMode: mode })
      persistProgress(get())
    },

    setActiveTutorialHint: (hint: string | null) => {
      set({ activeTutorialHint: hint })
    },

    setError: (error: UserError | null) => {
      set({ error })
    },

    clearError: () => {
      set({ error: null })
    },

    clearLastExecution: () => {
      set({ lastExecution: null })
    },
  }
})

// =============================================================================
// Selector Hooks
// =============================================================================

/**
 * Get the current market state (best bid/ask, spread, etc.).
 */
export function useMarketState() {
  const market = useAppStore(state => state.market)
  if (!market) return null
  return matchingEngine.getMarketState(market.orderBook)
}
